import { useEffect } from 'react';
import { isAuthenticated, redirectToLoginSync } from './authClient.js';
import { useAuth } from './useAuth.js';
import { useTenantContext } from '../api/tenant-context.js';

/**
 * Guard que:
 *  1. Garantiza sesión Cognito (sino → redirectToLogin).
 *  2. Valida que el usuario tenga rol `admin` (no `platform-ops`).
 *  3. Valida que su `custom:tenant_id` (UUID) resuelva a un tenant cuyo
 *     slug matchea el slug detectado en el hostname.
 *
 * Casos (2) y (3) renderizan una pantalla 403 explícita.
 */
export function AuthGuard({ children }: { children: React.ReactNode }): JSX.Element | null {
  const authed = isAuthenticated();
  useEffect(() => {
    if (!authed) redirectToLoginSync();
  }, [authed]);
  if (!authed) return null;
  return <TenantGate>{children}</TenantGate>;
}

function TenantGate({ children }: { children: React.ReactNode }): JSX.Element {
  const { claims } = useAuth();
  const { slug, tenant, isLoading, error } = useTenantContext();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-ink-3">
        Cargando contexto del tenant…
      </div>
    );
  }

  if (error) {
    return (
      <Forbidden
        title="No se pudo cargar el contexto del tenant"
        detail="Revisá tu conexión o intentá nuevamente."
      />
    );
  }

  // Aceptamos tanto `custom:roles` (preferido en ZE) como `cognito:groups`.
  const roles = [...(claims?.roles ?? []), ...(claims?.groups ?? [])];
  if (!roles.includes('admin')) {
    return (
      <Forbidden
        title="Acceso denegado"
        detail={`Tu usuario no tiene rol admin sobre este tenant. Roles: ${roles.join(', ') || '(ninguno)'}.`}
      />
    );
  }

  if (!slug) {
    return (
      <Forbidden
        title="Tenant no identificado"
        detail="No se pudo detectar el tenant a partir del subdominio. En local-dev, configurá `tenantSlugOverride` en public/config.js."
      />
    );
  }

  if (!tenant) {
    return (
      <Forbidden
        title="No tienes acceso a este tenant"
        detail={`Tu usuario está asignado a otro tenant. Slug solicitado: "${slug}".`}
      />
    );
  }

  if (claims?.tenantId && claims.tenantId !== tenant.id) {
    return (
      <Forbidden
        title="No tienes acceso a este tenant"
        detail={`Tu sesión pertenece a otro tenant (${claims.tenantId}). Slug solicitado: "${slug}".`}
      />
    );
  }

  return <>{children}</>;
}

function Forbidden({ title, detail }: { title: string; detail: string }): JSX.Element {
  // BUG-P0 (QA): si caemos en Forbidden el usuario quedaba atrapado sin
  // forma de cerrar sesión (Cognito SSO restablecía mismo token al recargar).
  // Importamos logout dinámicamente para no acoplar el componente.
  const handleLogout = async (): Promise<void> => {
    const { logout } = await import('./authClient.js');
    logout();
  };
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="panel max-w-lg text-center">
        <div className="text-2xl font-semibold mb-2">{title}</div>
        <p className="text-ink-3 text-sm">{detail}</p>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          Cerrar sesión y reintentar
        </button>
      </div>
    </div>
  );
}
